import { ApiError } from '@ghp/api-client'
import { newUuidV7 } from '@ghp/domain'
import type { ClientLogBatch, ClientLogEntry, ClientLogShipResult } from '@ghp/domain'

import type { LogEntry, SharedDatabase } from './shared-db'

/** Le seul appel dont l'expédition a besoin — injecté, comme le reste de la synchro. */
export interface LogShipApi {
  shipLogs(accessToken: string, batch: ClientLogBatch): Promise<ClientLogShipResult>
}

/**
 * Le journal local de la tablette (table `logs`) — la télémétrie du système (D15) : une
 * erreur vue sur le terrain ne se reproduit pas au bureau. Ici on consigne des **événements
 * structurés** ; l'expédition par lots (`shipLogs`, 5ᵉ étape de la synchro) les monte dans
 * `client_log` où ils se travaillent en SQL, et l'export local de l'écran Notifications reste
 * le plan B hors réseau.
 *
 * Trois principes :
 *
 * - **écrire ne coûte jamais rien au geste** : toute écriture est lancée sans être attendue,
 *   et son échec est avalé — un journal qui fait tomber l'app raconterait sa propre chute ;
 * - **tampon circulaire, pas archive** : une ligne montée au serveur est **marquée**, pas
 *   supprimée — l'export local rend tout le journal. Seul le plafond de
 *   {@link MAX_LOG_ENTRIES} fait partir des lignes, **les expédiées d'abord** : le quota
 *   d'IndexedDB appartient aux photos ;
 * - **jamais de donnée personnelle** : des identifiants, des états, des mesures — ni nom, ni
 *   téléphone, ni coordonnée GPS (le lieu d'une signature reste dans la proposition, M20).
 */

const MAX_LOG_ENTRIES = 10_000

/** La taille se vérifie par rafales, pas à chaque ligne : un `count()` par écriture doublerait le coût de chacune. */
const TRIM_EVERY = 100

/** Le plafond de lignes du contrat (`MAX_CLIENT_LOGS_PER_BATCH`) — plancher de lecture Dexie. */
const SHIP_BATCH_SIZE = 200

/**
 * La borne qui compte vraiment : des **octets**, pas des lignes. La route accepte 1 Mo
 * (`LOGS_BODY_LIMIT`), et 200 lignes aux plafonds d'ici — message 2 000, `detail` 4 000 —
 * pèsent davantage : le lot repartirait en `413` à chaque passe. La moitié du plafond, parce
 * qu'une ligne ne se coupe pas en deux et que l'estimation d'ici ignore la compression.
 */
const SHIP_BATCH_BYTES = 512 * 1024

/**
 * Les seuls refus qui visent le **contenu** du lot : lot invalide (400), corps trop gros (413),
 * lot bien formé mais refusé (422). Ceux-là ne se rejouent pas — le lot repartirait à chaque
 * passe et plus rien derrière lui ne monterait jamais. **Tout le reste se rejoue**, 404 et 405
 * compris : une route absente dit que l'API est en retard sur le bundle, pas que le lot est
 * mauvais — le jeter perdrait le journal sur un déploiement de travers, exactement ce que le
 * `looseObject` du contrat cherche à éviter.
 */
const PAYLOAD_REFUSAL_STATUSES = new Set([400, 413, 422])

/**
 * L'identité de l'émetteur. La session naît avec le chargement de la page ; l'appareil, lui,
 * survit — c'est le couple qui permet de recoudre « cette tablette, ce jour-là » en SQL.
 */
const SESSION_ID = newUuidV7()

const DEVICE_ID_KEY = 'ghp-terrain-device-id'

function deviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY)
    if (existing !== null) return existing

    const created = newUuidV7()
    localStorage.setItem(DEVICE_ID_KEY, created)
    return created
  } catch {
    // localStorage refusé (navigation privée verrouillée) : l'appareil vaut la session.
    return SESSION_ID
  }
}

const DEVICE_ID = deviceId()

/** L'horodatage de build, posé par Vite (`define`) — ce qui distingue deux déploiements. */
declare const __BUILD_AT__: string
const APP_BUILD = typeof __BUILD_AT__ === 'string' ? __BUILD_AT__ : 'dev'

let writesSinceTrim = 0

export interface RecordOptions {
  /** Le contrôle concerné, quand il y en a un. */
  inquiryId?: string
  /** Le contexte chiffré de l'événement — sérialisé et borné avant d'être rangé. */
  detail?: unknown
}

/**
 * Consigne un événement — **sans jamais faire attendre ni échouer l'appelant**. C'est la
 * seule porte d'écriture du journal : elle rend `void` à dessein.
 *
 * Le `tag` se déduit du nom de l'événement (`sync.sent` → `[sync]`) : un seul argument à ne
 * pas se tromper, et les deux ne divergent jamais.
 */
export function recordEvent(
  db: SharedDatabase,
  level: LogEntry['level'],
  event: string,
  message: string,
  options: RecordOptions = {},
): void {
  const entry: LogEntry = {
    at: new Date().toISOString(),
    sessionId: SESSION_ID,
    level,
    tag: clamp(`[${event.split('.')[0] ?? 'app'}]`, 32),
    event: clamp(event, 64),
    message: clamp(message, 2000),
    detail: options.detail === undefined ? null : boundedDetail(options.detail),
    inquiryId: options.inquiryId ?? null,
    shipped: 0,
  }

  void db.logs
    .add(entry)
    .then(() => {
      writesSinceTrim += 1
      if (writesSinceTrim >= TRIM_EVERY) {
        writesSinceTrim = 0
        return trim(db)
      }
    })
    .catch(() => {
      // Base illisible ou pleine : le journal est un confort, jamais une cause de panne —
      // et consigner cet échec dans le journal serait la récursion qu'on évite partout ici.
    })
}

/**
 * Branche la capture globale : erreurs JavaScript non rattrapées, promesses rejetées sans
 * preneur, transitions réseau et de visibilité, et les `console.error` / `console.warn` /
 * `console.info` que le dépôt émet déjà avec ses préfixes (`[terrain]`, `[sync]`, `[pwa]`).
 *
 * Envelopper la console plutôt que modifier chaque site d'appel : les messages existants
 * sont la télémétrie qu'on n'a pas — les réécrire un à un vers le journal ferait diverger
 * les deux au premier oubli. La console d'origine est **toujours** appelée d'abord.
 */
export function installLogCapture(db: SharedDatabase): void {
  window.addEventListener('error', (event) => {
    recordEvent(db, 'error', 'js.error', event.message, { detail: describe(event.error) })
  })

  window.addEventListener('unhandledrejection', (event) => {
    recordEvent(db, 'error', 'js.unhandled_rejection', 'Promesse rejetée sans preneur', {
      detail: describe(event.reason),
    })
  })

  // Les transitions réseau et de visibilité : c'est la trame de fond de tout diagnostic de
  // synchro — « la tablette était-elle en ligne à ce moment-là, l'app au premier plan ? ».
  window.addEventListener('online', () => recordEvent(db, 'info', 'app.online', 'Réseau retrouvé'))
  window.addEventListener('offline', () => recordEvent(db, 'warn', 'app.offline', 'Réseau perdu'))
  document.addEventListener('visibilitychange', () => {
    recordEvent(
      db,
      'info',
      'app.visibility',
      `L'app passe ${document.visibilityState === 'visible' ? 'au premier plan' : 'en arrière-plan'}`,
      {
        detail: { state: document.visibilityState },
      },
    )
  })

  const original = {
    error: console.error.bind(console),
    warn: console.warn.bind(console),
    info: console.info.bind(console),
  }

  // Garde anti-récursion : une écriture Dexie qui échoue peut elle-même passer par la
  // console (Dexie journalise certains échecs) — sans ce verrou, chaque ligne en produirait
  // une autre.
  let capturing = false

  for (const level of ['error', 'warn', 'info'] as const) {
    console[level] = (...args: unknown[]) => {
      original[level](...args)
      if (capturing) return
      // `info` n'entre que préfixé (`[pwa] …`) : le journal veut les messages du dépôt,
      // pas le bavardage des bibliothèques.
      if (level === 'info' && !(typeof args[0] === 'string' && args[0].startsWith('['))) return

      capturing = true
      try {
        const [head, ...rest] = args
        const message = typeof head === 'string' ? head : String(head)
        const prefix = /^\[([a-z-]+)\]/i.exec(message)?.[1] ?? 'app'
        recordEvent(db, level, `${prefix}.console`, message, {
          detail: rest.length > 0 ? rest.map(describe) : undefined,
        })
      } finally {
        capturing = false
      }
    }
  }
}

/**
 * L'instantané de démarrage — la première chose qu'un diagnostic demande : quelle tablette,
 * quel bundle, quel écran, combien de stockage. L'estimation de quota arrive en second
 * événement : elle est asynchrone, et le boot n'attend personne.
 */
export function recordBoot(db: SharedDatabase): void {
  recordEvent(db, 'info', 'app.boot', 'Démarrage de l’application', {
    detail: {
      build: APP_BUILD,
      online: navigator.onLine,
      url: location.pathname,
      userAgent: navigator.userAgent,
      screen: { width: window.screen.width, height: window.screen.height },
      language: navigator.language,
    },
  })

  void navigator.storage
    ?.estimate?.()
    .then((estimate) => {
      recordEvent(db, 'info', 'app.storage', 'État du stockage local', {
        detail: { usage: estimate.usage, quota: estimate.quota },
      })
    })
    .catch(() => {})
}

/**
 * Expédie le journal vers `POST /sync/logs`, par lots bornés, **sans jamais rejeter** — la
 * 5ᵉ étape de la passe de synchro. Une ligne expédiée passe à `shipped: 1` : **rien n'est
 * supprimé ici**, la ligne reste lisible et exportable, elle cesse seulement d'être candidate
 * au départ — c'est ce qui permet de lire le prochain lot sur l'index `shipped` au lieu de
 * balayer tout le tampon à chaque passe. Un échec ne marque rien : la passe suivante reprendra
 * là. Le rejeu d'un lot dont la réponse s'est perdue est inoffensif côté serveur
 * (`(deviceId, sessionId, seq)` unique, conflits ignorés).
 */
export async function shipLogs(
  db: SharedDatabase,
  api: LogShipApi,
  getToken: () => string | null,
): Promise<void> {
  // Quelques lots par passe au maximum : le journal ne doit jamais disputer la bande
  // passante aux photos — le reste partira à la passe suivante.
  for (let round = 0; round < 5; round++) {
    const accessToken = getToken()
    if (accessToken === null) return

    let pending: LogEntry[]
    try {
      // Pas de `.sortBy('seq')` : IndexedDB range les entrées d'un index non unique par
      // (clé d'index, clé primaire), et `seq` est un `++seq` numérique — `equals(0)` rend donc
      // déjà les lignes par `seq` croissant. `sortBy` chargerait toute la collection en
      // mémoire avant de trier, ce qui annulerait le bénéfice du `limit`.
      pending = await db.logs.where('shipped').equals(0).limit(SHIP_BATCH_SIZE).toArray()
    } catch {
      return
    }

    if (pending.length === 0) return

    const entries = boundedBatch(pending)
    const shipped = pending.slice(0, entries.length)

    let refused = false
    try {
      await api.shipLogs(accessToken, { entries })
    } catch (error) {
      // Réseau, 401 entre deux refresh, serveur en avance ou en retard : rien n'est marqué,
      // tout se rejouera. Le journal ne journalise pas ses propres ratés — récursion.
      if (!isPayloadRefusal(error)) return

      // Un refus qui vise le contenu, lui, ne se rejoue pas : le lot est **mis de côté** pour
      // débloquer ce qui le suit — il reste dans le journal et dans l'export, il ne repartira
      // simplement plus — et l'abandon est consigné (M21 : signaler, jamais effacer en silence).
      refused = true
      recordEvent(db, 'warn', 'sync.logs_dropped', 'Lot de journal refusé, lignes abandonnées', {
        detail: { status: error instanceof ApiError ? error.status : null, count: shipped.length },
      })
    }

    try {
      // Un marquage qui échoue laisse le lot à `0`, donc rejouable — et le rejeu est
      // inoffensif côté serveur (`(deviceId, sessionId, seq)` unique, conflits ignorés).
      await db.logs
        .where('seq')
        .anyOf(shipped.map((entry) => entry.seq ?? -1))
        .modify((entry) => {
          entry.shipped = 1
        })
    } catch {
      return
    }

    // Après un abandon, la passe s'arrête : la ligne qui vient de le consigner partira à la
    // suivante, et un serveur qui refuse tout ne marque pas le tampon entier en une fois.
    if (refused) return
  }
}

/**
 * Le début du lot qui tient sous {@link SHIP_BATCH_BYTES}. **Au moins une ligne** : une ligne
 * seule plus grosse que le budget part quand même — la route la refusera peut-être, et ce
 * refus la jettera ; la retenir bloquerait tout ce qui la suit, pour toujours.
 */
function boundedBatch(pending: readonly LogEntry[]): ClientLogEntry[] {
  const encoder = new TextEncoder()
  const batch: ClientLogEntry[] = []
  let bytes = 0

  for (const entry of pending) {
    const line = toClientLogEntry(entry)
    // Les octets, pas les caractères : un message accentué en pèse deux par lettre.
    bytes += encoder.encode(JSON.stringify(line)).length
    if (bytes > SHIP_BATCH_BYTES && batch.length > 0) break

    batch.push(line)
  }

  return batch
}

/** Un refus qui vise le contenu du lot — donc que le rejeu ne résoudra jamais. */
function isPayloadRefusal(error: unknown): boolean {
  return error instanceof ApiError && PAYLOAD_REFUSAL_STATUSES.has(error.status)
}

/** La ligne locale, dans la forme du contrat (`clientLogEntrySchema`). */
function toClientLogEntry(entry: LogEntry): ClientLogEntry {
  return {
    deviceId: DEVICE_ID,
    sessionId: entry.sessionId,
    seq: entry.seq ?? 0,
    at: entry.at,
    level: entry.level,
    tag: entry.tag,
    event: entry.event,
    message: entry.message,
    detail: entry.detail,
    inquiryId: entry.inquiryId,
    appBuild: APP_BUILD,
  }
}

/**
 * Ce que la tablette garde, en JSON prêt à être remis au bureau — le plan B du hors-réseau
 * total. C'est **tout** le journal encore présent, expédié compris : le bureau lit le même
 * récit que le SQL, sans trou entre ce qui est monté et ce qui attend. L'entête situe la
 * tablette : c'est ce qu'un fichier de lignes seules ne dit pas.
 */
export async function exportLogs(db: SharedDatabase): Promise<Blob> {
  const entries = await db.logs.orderBy('seq').toArray()

  const payload = {
    exportedAt: new Date().toISOString(),
    deviceId: DEVICE_ID,
    sessionId: SESSION_ID,
    build: APP_BUILD,
    userAgent: navigator.userAgent,
    online: navigator.onLine,
    entries,
  }

  return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
}

/** Taille le tampon au plafond du dépôt. Jamais bloquant, jamais bruyant. */
async function trim(db: SharedDatabase): Promise<void> {
  await trimTo(db, MAX_LOG_ENTRIES)
}

/**
 * Ramène le journal à `max` lignes, **les expédiées d'abord** (D15).
 *
 * Deux temps, et le second n'est pas un détail : une ligne jamais montée n'existe **qu'ici**,
 * donc on la sacrifie en dernier. Mais refuser de la sacrifier ferait buter le tampon contre
 * son plafond et bloquerait toute écriture nouvelle — et c'est l'écriture qui compte, une
 * erreur vue en tournée ne se reproduit pas au bureau.
 *
 * Le plafond est un paramètre plutôt qu'une constante lue ici : c'est ce qui rend la politique
 * vérifiable sans écrire dix mille lignes dans un test.
 */
export async function trimTo(db: SharedDatabase, max: number): Promise<void> {
  const count = await db.logs.count()
  if (count <= max) return
  let excess = count - max

  // 1. Les montées au serveur d'abord : le bureau les a déjà, leur perte ne coûte rien.
  //    L'ordre (clé d'index, clé primaire) de l'index les rend par `seq` croissant — donc les
  //    plus anciennes expédiées d'abord.
  //
  //    ⚠️ Une ligne **mise de côté** sur un refus de contenu porte le même `1` sans que
  //    personne ne la détienne : elle part donc ici la première, alors qu'elle est la seule
  //    copie qui existe — et ce sont justement les lignes au `detail` énorme, celles qui ont
  //    provoqué le refus, qu'on perd ainsi. Le remède est un troisième état (`2 = abandonnée`),
  //    donc un `version(n)` de plus : à trancher avec {@link MAX_LOG_ENTRIES}, pas à improviser
  //    ici. En attendant, la ligne `sync.logs_dropped` survit et dit qu'un lot a été abandonné.
  const redundant = await db.logs.where('shipped').equals(1).limit(excess).primaryKeys()
  if (redundant.length > 0) {
    await db.logs.bulkDelete(redundant)
    excess -= redundant.length
  }
  if (excess <= 0) return

  // 2. Un tampon saturé de lignes que le serveur n'a jamais vues : les plus anciennes partent.
  const oldest = await db.logs.orderBy('seq').limit(excess).primaryKeys()
  await db.logs.bulkDelete(oldest)
}

/** Une valeur quelconque, mise en forme sérialisable sans jamais lever. */
function describe(value: unknown): unknown {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: clamp(value.stack ?? '', 2000) }
  }
  if (typeof value === 'string') return clamp(value, 2000)

  try {
    // L'aller-retour JSON borne ce qui entre : pas d'objet vivant, pas de cycle. Un objet
    // trop bavard reste une **chaîne** tronquée — la retraduire en JSON serait invalide.
    const json = JSON.stringify(value)
    if (json === undefined) return String(value)
    if (json.length > 4000) return clamp(json, 4000)

    return JSON.parse(json) as unknown
  } catch {
    return String(value)
  }
}

/** Le contexte d'un événement, borné : un `detail` de 4 Ko est déjà un roman. */
function boundedDetail(detail: unknown): unknown {
  return describe(detail)
}

/** Une ligne de journal reste une ligne : au-delà, c'est le fichier exporté qui gonfle pour rien. */
function clamp(text: string, max: number): string {
  // L'ellipse **compte** dans la borne : `clamp(event, 64)` rendait 65 caractères, que le
  // schéma (`event: max(64)`) refusait — et le lot entier avec.
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}
